import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";

import crossIcon from "../../assets/img/icons/cross_black.svg";
import tickSquareIcon from "../../assets/img/upgrade-plan/tick-square.svg";
import arrowRightIcon from "../../assets/img/upgrade-plan/arrow-right.svg";
import discountBadgeShape from "../../assets/img/upgrade-plan/discount-badge.svg";
import { paddleCheckout } from "./Paddle";
import { getPlanType } from "../../utils/helpers";

function buildFeatures(groupLimit) {
  return [
    `Add up to ${groupLimit} Facebook groups`,
    "Unlimited members approval and lead collection",
    "Access to Google Sheet integration",
    "Integration with popular email marketing autoresponders",
    "Lifetime update to software",
    "Option to export data both in CSV and Excel format from dashboard.",
    "Create FB custom and lookalike audience",
    "VIP Facebook group for users",
    "Chat support",
    "14 days refund guarantee",
    "Exclusive support through Zoom/Google Meet/Teams",
  ];
}

// Monthly Plans
const monthlyPackages = [
  {
    name: "Monthly Basic",
    tierLabel: "Basic",
    price: "19",
    groupLimit: 2,
    features: buildFeatures(2),
    id: parseInt(process.env.REACT_APP_MONTHLY_PLAN_1, 10),
  },
  {
    name: "Monthly Pro",
    tierLabel: "Pro",
    price: "29",
    groupLimit: 4,
    features: buildFeatures(4),
    id: parseInt(process.env.REACT_APP_MONTHLY_PLAN_2, 10),
    badge: "Most Popular",
  },
  {
    name: "Monthly Mega",
    tierLabel: "Mega",
    price: "49",
    groupLimit: 20,
    features: buildFeatures(20),
    id: parseInt(process.env.REACT_APP_MONTHLY_PLAN_3, 10),
  },
];

// Yearly Plans
const yearlyPackages = [
  {
    name: "Yearly Basic",
    tierLabel: "Basic",
    price: "79",
    groupLimit: 2,
    features: buildFeatures(2),
    id: parseInt(process.env.REACT_APP_YEARLY_PLAN_1, 10),
  },
  {
    name: "Yearly Pro",
    tierLabel: "Pro",
    price: "99",
    groupLimit: 4,
    features: buildFeatures(4),
    id: parseInt(process.env.REACT_APP_YEARLY_PLAN_2, 10),
    badge: "Most Popular",
  },
  {
    name: "Yearly Mega",
    tierLabel: "Mega",
    price: "189",
    groupLimit: 20,
    features: buildFeatures(20),
    id: parseInt(process.env.REACT_APP_YEARLY_PLAN_3, 10),
  },
];

function UpgradePlanCard({
  item,
  billingPeriod,
  isCurrent,
  isFeatured,
  onChoose,
}) {
  const priceSuffix = billingPeriod === "yearly" ? "/ Year" : "/ Month";

  const cardInner = (
    <>
      <div className="upgrade-plan-card__top">
        <div className="upgrade-plan-title-pill">{item.tierLabel}</div>
        <div className="upgrade-plan-price-row">
          <span className="upgrade-plan-price">${item.price}</span>
          <span className="upgrade-plan-price-suffix">{priceSuffix}</span>
        </div>
      </div>
      <ul className="upgrade-plan-features">
        {item.features.map((text, i) => (
          <li key={i} className="upgrade-plan-feature">
            <img
              src={tickSquareIcon}
              alt=""
              className="upgrade-plan-feature__icon"
              width={24}
              height={24}
            />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="upgrade-plan-cta"
        onClick={onChoose}
        disabled={isCurrent}
      >
        <span className="upgrade-plan-cta__arrow" aria-hidden="true">
          <img src={arrowRightIcon} alt="" width={24} height={24} />
        </span>
        <span className="upgrade-plan-cta__label">Choose plan</span>
      </button>
    </>
  );

  return (
    <div
      className={`upgrade-plans-grid__cell ${!isFeatured ? "upgrade-plans-grid__cell--offset" : ""}`}
    >
      <div
        className={`upgrade-plan-card-host ${isCurrent ? "upgrade-plan-card-host--current" : ""} ${isFeatured ? "upgrade-plan-card-host--featured" : ""}`}
      >
        {isFeatured ? (
          <div className="upgrade-plan-featured-shell">
            <div className="upgrade-plan-best-deal">Best Deal</div>
            <div className="upgrade-plan-card upgrade-plan-card--featured-inner">
              {cardInner}
            </div>
          </div>
        ) : (
          <div className="upgrade-plan-card upgrade-plan-card--standard">
            {cardInner}
          </div>
        )}
        {isCurrent && (
          <div className="upgrade-plan-current-overlay">
            <span className="upgrade-plan-current-badge">Current Plan</span>
          </div>
        )}
      </div>
    </div>
  );
}

function UpgradePlanModal({
  modal,
  setModal,
  planStatus,
  planId,
  userMail,
  initiatePlanUpgrade,
}) {
  const [billingPeriod, setBillingPeriod] = useState("yearly");

  const openCheckout = (id, name) => {
    paddleCheckout(id, name, userMail);
  };

  useEffect(() => {
    if (planId) {
      const planType = getPlanType(planId);
      if (planType === "monthly") {
        setBillingPeriod("monthly");
      } else {
        setBillingPeriod("yearly");
      }
    }
  }, [planId]);

  const packages =
    billingPeriod === "monthly" ? monthlyPackages : yearlyPackages;

  const toggle = () =>
    setModal({
      upgradeModal: !modal.upgradeModal,
    });

  const handleChoosePlan = (item) => {
    if (
      planStatus === "deleted" ||
      !planStatus ||
      planStatus === "inactive"
    ) {
      openCheckout(item.id, item.name);
    } else {
      initiatePlanUpgrade({
        plan_id: item.id,
      });
    }
    toggle();
  };

  const isCurrentPlan = (item) =>
    parseInt(item.id, 10) === parseInt(planId, 10) &&
    planStatus !== "deleted";

  return (
    <div>
      <Modal
        isOpen={modal.upgradeModal}
        toggle={toggle}
        backdrop={true}
        keyboard={true}
        centered={true}
        size="xl"
        className="upgrade-modal"
      >
        <button
          type="button"
          className="cross-icon upgrade-modal__close"
          onClick={toggle}
          aria-label="Close"
        >
          <img src={crossIcon} alt="" width={18} height={18} />
        </button>

        <ModalBody>
          <div className="upgrade-modal-body">
            <div className="upgrade-header">
              <h3>Choose the plan that fits your growth</h3>
              <p>Simple pricing. Powerful results. Cancel anytime.</p>
            </div>

            <div className="upgrade-plan-billing-row">
              <div className="upgrade-plan-toggle-wrap">
                <div className="upgrade-plan-save-badge">
                  <img
                    src={discountBadgeShape}
                    alt=""
                    className="upgrade-plan-save-badge__shape"
                    width={56}
                    height={56}
                  />
                  <div className="upgrade-plan-save-badge__text">
                    <span className="upgrade-plan-save-badge__pct">60%+</span>
                    <span className="upgrade-plan-save-badge__save">Save</span>
                  </div>
                </div>
                <div
                  className="upgrade-plan-toggle-pair"
                  role="group"
                  aria-label="Billing period"
                >
                  <button
                    type="button"
                    className={`upgrade-plan-toggle-btn upgrade-plan-toggle-btn--yearly ${billingPeriod === "yearly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("yearly")}
                    aria-pressed={billingPeriod === "yearly"}
                  >
                    Yearly
                  </button>
                  <button
                    type="button"
                    className={`upgrade-plan-toggle-btn upgrade-plan-toggle-btn--monthly ${billingPeriod === "monthly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("monthly")}
                    aria-pressed={billingPeriod === "monthly"}
                  >
                    Monthly
                  </button>
                </div>
              </div>
            </div>

            <div className="upgrade-plans-grid">
              {packages.map((item, index) => (
                <UpgradePlanCard
                  key={`${item.id}-${index}`}
                  item={item}
                  billingPeriod={billingPeriod}
                  isCurrent={isCurrentPlan(item)}
                  isFeatured={item.badge === "Most Popular"}
                  onChoose={() => handleChoosePlan(item)}
                />
              ))}
            </div>

            <div className="upgrade-footer-hint">
              <span>
                14-day money-back guarantee • No hidden fees • Secure checkout
              </span>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default UpgradePlanModal;
